import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import timesheetService from '../../services/timesheetService';

const initialState = {
  activeTimer: null,
  timeEntries: [],
  loading: false,
  error: null,
  workNoteDialogOpen: false,
  switchTaskDialogOpen: false,
  targetTask: null,
  notification: null, // { message: string, severity: 'success' | 'error' | 'info' }
};

// Async Thunks

export const fetchActiveTimer = createAsyncThunk(
  'timesheet/fetchActiveTimer',
  async (_, { rejectWithValue }) => {
    try {
      const response = await timesheetService.getActiveTimer();
      // Handle empty response (204 No Content) as null
      if (!response.data || response.data === '' || response.status === 204) {
        return null;
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to fetch active timer' });
    }
  }
);

export const startTimer = createAsyncThunk(
  'timesheet/startTimer',
  async (taskId, { rejectWithValue }) => {
    try {
      const response = await timesheetService.startTimer(taskId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to start timer' });
    }
  }
);

export const stopTimer = createAsyncThunk(
  'timesheet/stopTimer',
  async (workNote, { rejectWithValue }) => {
    try {
      const response = await timesheetService.stopTimer(workNote);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to stop timer' });
    }
  }
);

export const switchTask = createAsyncThunk(
  'timesheet/switchTask',
  async ({ newTaskId, workNote }, { rejectWithValue }) => {
    try {
      const response = await timesheetService.switchTask(newTaskId, workNote);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to switch task' });
    }
  }
);

export const fetchTimeEntries = createAsyncThunk(
  'timesheet/fetchTimeEntries',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await timesheetService.getTimeEntries(filters);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to fetch time entries' });
    }
  }
);

// Slice

const timesheetSlice = createSlice({
  name: 'timesheet',
  initialState,
  reducers: {
    setWorkNoteDialogOpen: (state, action) => {
      state.workNoteDialogOpen = action.payload;
    },
    setSwitchTaskDialogOpen: (state, action) => {
      state.switchTaskDialogOpen = action.payload;
    },
    setTargetTask: (state, action) => {
      state.targetTask = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setNotification: (state, action) => {
      state.notification = action.payload;
    },
    clearNotification: (state) => {
      state.notification = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Active Timer
      .addCase(fetchActiveTimer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveTimer.fulfilled, (state, action) => {
        state.loading = false;
        state.activeTimer = action.payload;
      })
      .addCase(fetchActiveTimer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Unknown error';
      })

      // Start Timer
      .addCase(startTimer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startTimer.fulfilled, (state, action) => {
        state.loading = false;
        state.activeTimer = action.payload;
        state.notification = {
          message: 'Timer started successfully!',
          severity: 'success',
        };
      })
      .addCase(startTimer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Unknown error';
        state.notification = {
          message: action.payload?.error || 'Failed to start timer. Please ensure you have an employee record.',
          severity: 'error',
        };
      })

      // Stop Timer
      .addCase(stopTimer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(stopTimer.fulfilled, (state, action) => {
        state.loading = false;
        state.activeTimer = null;
        state.workNoteDialogOpen = false;
        state.notification = {
          message: 'Timer stopped successfully!',
          severity: 'success',
        };
      })
      .addCase(stopTimer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Unknown error';
        state.notification = {
          message: action.payload?.error || 'Failed to stop timer',
          severity: 'error',
        };
      })

      // Switch Task
      .addCase(switchTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(switchTask.fulfilled, (state, action) => {
        state.loading = false;
        state.activeTimer = action.payload.started_timer;
        state.switchTaskDialogOpen = false;
        state.targetTask = null;
        state.notification = {
          message: 'Switched to new task successfully!',
          severity: 'success',
        };
      })
      .addCase(switchTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Unknown error';
        state.notification = {
          message: action.payload?.error || 'Failed to switch task',
          severity: 'error',
        };
      })

      // Fetch Time Entries
      .addCase(fetchTimeEntries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTimeEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.timeEntries = action.payload;
      })
      .addCase(fetchTimeEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Unknown error';
      });
  },
});

export const {
  setWorkNoteDialogOpen,
  setSwitchTaskDialogOpen,
  setTargetTask,
  clearError,
  setNotification,
  clearNotification,
} = timesheetSlice.actions;

export default timesheetSlice.reducer;
